/**
 * Mobile Navigation Module
 * Handles mobile navigation functionality
 */

import { BaseNavigation } from './base-navigation.js';
import { onDOMReady, devLog } from './utils.js';

class MobileNavigation extends BaseNavigation {
    constructor() {
        super({ selector: '#mobile-navigation' });
        this.mobileNav = this.nav;
        this.body = document.body;
        this.init();
    }

    init() {
        if (!this.mobileNav) {
            this.retryInit();
            return;
        }
        this.createExpandButtons();
        this.bindEvents();
        this.initializeToggles();
        this.checkMobileState();
    }

    retryInit() {
        let attempts = 0;
        const maxAttempts = 10;
        const retry = () => {
            attempts++;
            this.mobileNav = document.querySelector('#mobile-navigation');
            if (this.mobileNav) {
                this.init();
            } else if (attempts < maxAttempts) {
                setTimeout(retry, 100);
            }
        };
        retry();
    }

    bindEvents() {
        super.bindEvents();
        this.bindMobileEvents();
    }

    bindMobileEvents() {
        const openButtons = document.querySelectorAll('.mobile-menu-toggle, .mobile-menu-trigger');
        
        openButtons.forEach((button, index) => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.openMobileNav();
            });
        });
        
        const closeButton = this.mobileNav?.querySelector('.mobile-menu-close');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.closeMobileNav();
            });
        }
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.mobileNav?.classList.contains('is-open')) {
                this.closeMobileNav();
            }
        });
        if (this.mobileNav) {
            this.mobileNav.addEventListener('click', (e) => {
                if (e.target === this.mobileNav) {
                    this.closeMobileNav();
                }
            });
        }
    }

    createExpandButtons() {
        if (!this.mobileNav) return;
        // Find the actual menu list (ul) inside the mobile nav container
        const menuList = this.mobileNav.querySelector('ul');
        if (!menuList) return;
        
        // Only create expand buttons for first-level menu items (depth 0)
        const allMenuItems = this.mobileNav.querySelectorAll('.menu-item-has-children');
        
        let firstLevelCount = 0;
        allMenuItems.forEach((menuItem, index) => {
            // Check if this is a first-level item (direct child of the menu list)
            if (menuItem.parentElement === menuList) {
                firstLevelCount++;
                
                if (menuItem.querySelector('.menu-toggle')) {
                    return;
                }
                const hasSubmenu = menuItem.querySelector('.sub-menu, .mega-menu-grid');
                if (!hasSubmenu) {
                    return;
                }
                
                let wrapper = menuItem.querySelector('.menu-item-wrapper');
                if (!wrapper) {
                    const link = menuItem.querySelector('a');
                    if (link) {
                        wrapper = document.createElement('div');
                        wrapper.className = 'menu-item-wrapper';
                        link.parentNode.insertBefore(wrapper, link);
                        wrapper.appendChild(link);
                    }
                }
                if (wrapper) {
                    menuItem.classList.add('has-submenu');
                    const toggle = document.createElement('button');
                    toggle.className = 'menu-toggle';
                    toggle.setAttribute('aria-expanded', 'false');
                    toggle.setAttribute('aria-label', 'Toggle submenu');
                    toggle.innerHTML = `
                        <svg class="menu-toggle-icon" width="15" height="10" viewBox="0 0 15 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1L7.5 8L14 1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    `;
                    wrapper.appendChild(toggle);
                }
            }
            // Skip nested items (they won't get toggles)
        });
    }

    initializeToggles() {
        if (!this.mobileNav) return;
        const toggles = this.mobileNav.querySelectorAll('.menu-item-wrapper > .menu-toggle');
        toggles.forEach(toggle => {
            toggle.replaceWith(toggle.cloneNode(true));
        });
        const freshToggles = this.mobileNav.querySelectorAll('.menu-item-wrapper > .menu-toggle');
        freshToggles.forEach(toggle => {
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleSubmenu(toggle);
            });
        });
    }

    toggleSubmenu(toggle) {
        const menuItem = toggle.closest('.menu-item');
        if (!menuItem) return;
        const submenu = menuItem.querySelector('.sub-menu, .mega-menu-grid');
        if (submenu) {
            super.toggleSubmenu(toggle, submenu);
        }
    }

    openMobileNav() {
        if (!this.mobileNav) {
            return;
        }
        
        this.mobileNav.classList.add('is-open');
        this.body.classList.add('mobile-navigation-open');
        
        console.log('[DEBUG] MobileNavigation: Classes after adding:', this.mobileNav.className);
        
        const firstFocusable = this.mobileNav.querySelector('button, input, a[href]');
        if (firstFocusable) {
            firstFocusable.focus();
        }
        
        console.log('[DEBUG] MobileNavigation: Mobile nav opened successfully');
    }

    closeMobileNav() {
        if (!this.mobileNav) return;
        this.mobileNav.classList.remove('is-open');
        this.body.classList.remove('mobile-navigation-open');
        const openToggles = this.mobileNav.querySelectorAll('.menu-toggle.is-open');
        openToggles.forEach(toggle => {
            const menuItem = toggle.closest('.menu-item');
            if (menuItem) {
                const submenu = menuItem.querySelector('.sub-menu, .mega-menu-grid');
                if (submenu) {
                    this.closeSubmenu(toggle, submenu);
                }
            }
        });
    }
}

export default MobileNavigation; 